import { useEffect, useMemo, useRef, useState, type VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../../../../contexts/ToastContext';
import { window } from '../../../../../../../globals';
import { useCreateReportOption } from '../../../../../../../hooks/api/reports/useCreateReportOption';
import { useDeleteReportOption } from '../../../../../../../hooks/api/reports/useDeleteReportOption';
import useReportOptions from '../../../../../../../hooks/api/reports/useReportOptions';
import { useUpdateReportOption } from '../../../../../../../hooks/api/reports/useUpdateReportOption';
import { getErrorMessage } from '../../../../../../../ReactQuery/errors';
import DeleteModal from '../../../../../../generic/SavedResources/modals/DeleteModal';
import RenameModal from '../../../../../../generic/SavedResources/modals/RenameModal';
import SaveAsModal from '../../../../../../generic/SavedResources/modals/SaveAsModal';
import { type SavedResourceActionType } from '../../../../../../generic/SavedResources/SavedResourceDropdownActions';
import {
  SavedResourceDropdownContainer,
} from '../../../../../../generic/SavedResources/SavedResourceDropdownContainer';
import { type ResourceOption } from '../../../../../../generic/SavedResources/SavedResourceDropdownList';
import type { PartialReportConfig } from '../../../../../../../../shared/reports/types';

import { useSavedOptionsPermissions } from './useSavedOptionsPermissions';

type ActiveModal = 'saveAs' | 'rename' | 'delete' | null;

export type SavedOptionApplySource = 'selection' | 'reset' | 'save' | 'delete';

type Props = {
  currentConfig: PartialReportConfig;
  onApply: (config: PartialReportConfig | null, source?: SavedOptionApplySource) => void;
  isSavedOptionDirty: boolean;
  isRestricted: boolean;
  initialOptionId?: number | null;
};

export const SavedOptions: VoidFunctionComponent<Props> = ({
  currentConfig,
  onApply,
  isSavedOptionDirty,
  isRestricted,
  initialOptionId,
}) => {
  const { canCreate, canEdit, canDelete } = useSavedOptionsPermissions();

  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  const addToast = useAddToast();

  // We can only fetch options if the user is authenticated and has an organization ID.
  const organizationId = window.user?.organizationId || 0;
  const shouldFetch = organizationId > 0 && !isRestricted;
  const { data: options, isLoading } = useReportOptions(organizationId, shouldFetch);

  const createMutation = useCreateReportOption(organizationId);
  const updateMutation = useUpdateReportOption(organizationId);
  const deleteMutation = useDeleteReportOption(organizationId);

  const selectOptions: ResourceOption[] = useMemo(
    () => [
      { id: null, name: 'None' },
      ...(options?.results.map((option) => ({ id: option.id, name: option.name })) || []),
    ],
    [options],
  );
  const currentOption = useMemo(
    () => selectOptions.find((option) => option.id === selectedOptionId) || { id: null, name: 'None' },
    [selectOptions, selectedOptionId],
  );

  const lastAppliedDefaultId = useRef<number | null>(null);

  useEffect(() => {
    if (
      initialOptionId != null &&
      initialOptionId !== lastAppliedDefaultId.current &&
      !isLoading &&
      options?.results
    ) {
      if (selectedOptionId !== initialOptionId) {
        const match = options.results.find((o) => o.id === initialOptionId);
        if (match) {
          lastAppliedDefaultId.current = initialOptionId;
          setSelectedOptionId(initialOptionId);
          onApply(match.options);
        }
      } else {
        lastAppliedDefaultId.current = initialOptionId;
      }
    }
  }, [initialOptionId, isLoading, options, selectedOptionId, onApply]);

  const onChangeSelection = useMemo(
    () => (optionId: number | null) => {
      setSelectedOptionId(optionId);
      const selected = options?.results.find((o) => o.id === optionId);
      onApply(selected?.options ?? null, 'selection');
    },
    [options, onApply],
  );

  const onSave = () => {
    if (selectedOptionId === null) {
      addToast({
        type: 'error',
        content: 'No saved option selected',
      });
      return;
    }
    const savedOption = options?.results.find((o) => o.id === selectedOptionId);
    if (!savedOption) {
      addToast({
        type: 'error',
        content: 'Saved option not found',
      });
      return;
    }
    updateMutation.mutateAsync(
      {
        id: selectedOptionId,
        data: { name: savedOption.name, options: currentConfig },
      },
      { onSuccess: () => onApply(currentConfig, 'save') },
    );
  };

  const onSaveAs = (name: string) => {
    createMutation.mutate(
      { name, options: currentConfig },
      {
        onSuccess: (response) => {
          setActiveModal(null);
          if (response.data?.id != null) {
            setSelectedOptionId(response.data.id);
          }
        },
      },
    );
  };

  const onRename = () => {
    updateMutation.reset();
    setActiveModal('rename');
  };

  const onRenameSubmit = (name: string) => {
    if (selectedOptionId === null) {
      return;
    }
    const savedOption = options?.results.find((o) => o.id === selectedOptionId);
    if (!savedOption) {
      return;
    }
    updateMutation.mutate(
      { id: selectedOptionId, data: { name, options: savedOption.options } },
      { onSuccess: () => setActiveModal(null) },
    );
  };

  const onDelete = () => {
    deleteMutation.reset();
    setActiveModal('delete');
  };

  const onDeleteConfirm = () => {
    if (selectedOptionId === null) {
      return;
    }
    deleteMutation.mutate(selectedOptionId, {
      onSuccess: () => {
        setActiveModal(null);
        setSelectedOptionId(null);
        onApply(null, 'delete');
      },
    });
  };

  const isMutationLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const actions = [
    {
      label: 'Reset',
      onSelect: () => {
        const selected = options?.results.find((o) => o.id === selectedOptionId);
        onApply(selected?.options ?? null, 'reset');
      },
      isDisabled: !isSavedOptionDirty,
    },
    {
      label: 'Save',
      onSelect: onSave,
      isDisabled: !(canCreate && canEdit) || !isSavedOptionDirty || selectedOptionId === null,
    },
    {
      label: 'Save As...',
      onSelect: () => setActiveModal('saveAs'),
      isDisabled: !canCreate,
    },
    {
      label: 'Rename...',
      onSelect: onRename,
      isDisabled: !canEdit || selectedOptionId === null,
    },
    {
      label: 'Delete',
      onSelect: onDelete,
      isDisabled: !canDelete || selectedOptionId === null,
    },
  ];

  const onChange = (label: SavedResourceActionType) => actions.find((action) => action.label === label)?.onSelect();

  return (
    <>
      <SavedResourceDropdownContainer
        resourceType="option"
        resources={selectOptions}
        currentResource={currentOption}
        isLoading={isLoading}
        onChangeDropdownList={onChangeSelection}
        actions={actions}
        isDisabled={isMutationLoading}
        hasAnyEditPermission
        onChangeDropdownActions={onChange}
        hasNotificationDot={isSavedOptionDirty}
        shouldHideId
        isRestricted={!shouldFetch}
      />
      <SaveAsModal
        resourceType="option"
        isDisplayed={activeModal === 'saveAs'}
        isSaving={createMutation.isPending}
        onSave={onSaveAs}
        onHide={() => setActiveModal(null)}
        errors={createMutation.error ? [getErrorMessage(createMutation.error)] : null}
      />
      {/* Putting this conditional check here is important to force a re-render of the modal each time it's opened.
       * Without it, the modal wouldn't have access to the latest state of the component and would render a stale
       * name in the pre-filled input field. */}
      {activeModal === 'rename' && (
        <RenameModal
          resourceType="option"
          currentName={currentOption.name}
          isDisplayed
          isSaving={updateMutation.isPending}
          onRename={onRenameSubmit}
          onHide={() => {
            updateMutation.reset();
            setActiveModal(null);
          }}
          errors={updateMutation.error ? [getErrorMessage(updateMutation.error)] : null}
        />
      )}
      {activeModal === 'delete' && (
        <DeleteModal
          resourceType="option"
          resourceName={currentOption.name}
          isDisplayed
          isDeleting={deleteMutation.isPending}
          onDelete={onDeleteConfirm}
          onHide={() => setActiveModal(null)}
          errors={deleteMutation.error ? [getErrorMessage(deleteMutation.error)] : null}
        />
      )}
    </>
  );
};
